import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { supabase } from "../lib/supabaseClient";
import { inferMediumCategory } from "../utils/mediumCategory";
import ImportJobsList from "./ImportJobsList";

// ─── Champs cibles ────────────────────────────────────────────────────────────
const TARGET_FIELDS = [
  "title", "artist", "year", "medium", "description", "dimensions", "tags", "typeOfArt",
  "estimated_value", "currency", "value", "sale_price",
  "inventory_number",
  "provenance",
  "acquisition_date", "acquisition_price", "acquisition_type", "acquisition_source",
  "last_estimation_date",
  "availability", "condition", "conditionNotes",
  "isEdition", "editionInfo", "signature", "signature_desc",
  "location_name", // texte libre → résolu en UUID avant envoi
];

const FIELD_LABELS = {
  title: "Titre de l'œuvre",
  artist: "Artiste (nom complet)",
  year: "Année",
  medium: "Technique / medium",
  description: "Description",
  dimensions: "Dimensions",
  tags: "Mots-clés (séparés par des virgules)",
  typeOfArt: "Type d'œuvre",
  estimated_value: "Valeur estimée",
  currency: "Devise (EUR, USD…)",
  value: "Valeur numérique (optionnel)",
  price: "Prix de référence historique (ex: prix d'achat initial)",
  sale_price: "Prix de vente",
  inventory_number: "Numéro d'inventaire",
  provenance: "Provenance (galerie, maison de vente…)",
  acquisition_date: "Date d'acquisition",
  acquisition_price: "Prix d'acquisition",
  acquisition_type: "Type d'acquisition (achat, héritage, donation…)",
  acquisition_source: "Source (galerie, maison de vente, particulier…)",
  last_estimation_date: "Date de dernière estimation",
  availability: "Disponibilité (En collection, En vente…)",
  is_public_for_sale: "Mettre en vente sur la marketplace Artkiv",
  condition: "État de conservation",
  conditionNotes: "Commentaires sur l'état",
  isEdition: "Œuvre en édition ? (true/false)",
  editionInfo: "Infos d'édition (ex: 2/8 + 2EA)",
  signature: "Signée ? (true/false)",
  signature_desc: "Détails sur la signature",
  location_name: "Lieu de stockage",
};


// ─── Infobulles explicatives pour les champs sensibles ────────────────────────
const FIELD_HINTS = {
  is_public_for_sale: "Par sécurité, ce champ est réglé sur Non par défaut. Aucune œuvre ne sera visible sur la marketplace sans votre accord explicite.",
  price: "Prix de référence, distinct du prix d'acquisition. Peut correspondre au prix d'achat initial, au prix coté, ou à toute valeur de référence historique que vous souhaitez conserver.",
  availability: "Statut actuel de l'œuvre dans votre collection. 'En collection' est le statut standard pour une œuvre conservée.",
  acquisition_type: "Comment l'œuvre est entrée dans votre patrimoine. Si elle vient d'un don ou d'un héritage, cela peut avoir des implications fiscales.",
  provenance: "Historique de propriété de l'œuvre avant vous. Important pour l'authenticité et la valeur. Vous pouvez mettre le nom de la galerie ou du vendeur.",
  inventory_number: "Référence unique de l'œuvre dans votre inventaire. Artkiv l'utilise pour éviter les doublons. Si votre fichier utilise la même référence pour plusieurs œuvres (lots), vous pouvez auto-numéroter.",
  currency: "Devise dans laquelle sont exprimées les valeurs financières de cet import.",
};


// ─── Champs avec valeur fixe possible ────────────────────────────────────────
const PRESET_FIELDS = {
  acquisition_type:    { options: ["Achat","Don","Héritage","Legs","Échange","Autre"] },
  acquisition_source:  { options: ["Galerie","Maison de vente","Particulier","Succession","Artiste","Autre"] },
  provenance:          { options: ["Galerie","Maison de vente","Particulier","Succession","Artiste","Autre"] },
  availability:        { options: ["En collection","En prêt","En vente","Vendu","Perdu","Volé"] },
  isEdition:           { boolean: true, options: ["Non","Oui"] },
  signature:           { boolean: true, options: ["Non","Oui"] },
  currency:            { options: ["EUR","USD","GBP","CHF","JPY","CNY"] },
};

function parseBoolean(val) {
  if (val === true  || val === "true"  || val === "Oui" || val === "1") return true;
  if (val === false || val === "false" || val === "Non" || val === "0") return false;
  return null;
}

function parseSheet(ws) {
  // Lecture brute : chaque ligne = tableau de valeurs, sans nommage auto
  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

  // Trouver la ligne d'en-têtes (la plus dense avec valeurs uniques)
  let bestIdx = 0, bestScore = -1;
  rawRows.slice(0, 10).forEach((row, i) => {
    const nonEmpty = row.filter((c) => c !== "" && c !== null && c !== undefined);
    const unique = new Set(nonEmpty.map((c) => String(c).trim().toLowerCase()));
    const score = nonEmpty.length + (unique.size === nonEmpty.length ? nonEmpty.length : 0);
    if (score > bestScore) { bestScore = score; bestIdx = i; }
  });

  // Construire les en-têtes depuis cette ligne, en ignorant les colonnes vides
  const headerRow = rawRows[bestIdx];
  // On garde uniquement les indices qui ont un en-tête non-vide
  const colMap = []; // [{idx, name}]
  headerRow.forEach((cell, idx) => {
    const name = String(cell).trim();
    if (name) colMap.push({ idx, name });
  });

  // Construire les objets pour les lignes de données (après l'en-tête)
  const dataRows = rawRows.slice(bestIdx + 1);
  return dataRows
    .map((row) => {
      const obj = {};
      colMap.forEach(({ idx, name }) => {
        obj[name] = row[idx] ?? "";
      });
      return obj;
    })
    // Ignorer les lignes complètement vides
    .filter((obj) => Object.values(obj).some((v) => v !== "" && v !== null && v !== undefined));
}

// ─── Utilitaires ──────────────────────────────────────────────────────────────
function parseDimensions(raw) {
  if (!raw) return { height: null, width: null, depth: null, unit: "cm" };
  const cleaned = String(raw).replace(/×/g, "x").replace(/X/g, "x").replace(/\s+/g, "").trim();
  // Remplacer virgule décimale française avant parseFloat
  const parts = cleaned.split("x").map((p) => parseFloat(p.replace(",", "."))).filter((n) => !isNaN(n));
  if (parts.length === 2) return { height: parts[0], width: parts[1], depth: null, unit: "cm" };
  if (parts.length === 3) return { height: parts[0], width: parts[1], depth: parts[2], unit: "cm" };
  return { height: null, width: null, depth: null, unit: "cm" };
}

function cleanValue(val) {
  return (val === "" || val === null || val === undefined) ? null : val;
}

function toNumber(val) {
  if (val === "" || val === null || val === undefined) return null;
  const cleaned = String(val).replace(/[€$£\s\u00a0\u202f]/g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

function excelDateToISO(excelDate) {
  if (!excelDate) return null;
  const str = String(excelDate).trim();
  if (str.match(/^\d{4}-\d{2}-\d{2}$/)) return str;
  if (str.match(/^\d{4}$/)) return null; // année seule → pas une date
  const fr = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (fr) return `${fr[3]}-${fr[2].padStart(2,"0")}-${fr[1].padStart(2,"0")}`;
  const days = typeof excelDate === "number" ? excelDate : parseFloat(str.replace(",","."));
  if (!isNaN(days) && days > 30000 && days < 60000)
    return new Date((days - 25569) * 86400 * 1000).toISOString().split("T")[0];
  return null;
}

function autoMap(firstRow) {
  const keys = Object.keys(firstRow || {});
  const auto = {};
  TARGET_FIELDS.forEach((tf) => {
    const exact = keys.find((h) => h.toLowerCase() === tf.toLowerCase());
    if (exact) auto[tf] = exact;
  });
  return auto;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  select: {
    flex: 1, border: "1px solid #e5e5e5", borderRadius: 2,
    padding: "0.3rem 0.5rem", fontSize: "0.85rem",
    fontFamily: "Georgia, serif", background: "#fff",
  },
  label: { width: 220, fontSize: "0.85rem", color: "#444", flexShrink: 0 },
  box: { border: "1px solid #e5e5e5", borderRadius: 2, padding: "1.25rem" },
  sectionTitle: { fontWeight: 400, fontSize: "0.95rem", letterSpacing: "0.04em", marginBottom: "1rem" },
  hint: { fontSize: "0.78rem", color: "#aaa", marginTop: "0.75rem" },
  specialBox: {
    gridColumn: "1 / -1", border: "1px solid #f0e8d0",
    borderRadius: 2, padding: "0.75rem 1rem", background: "#fffdf7",
  },
  toggleBtn: (active) => ({
    padding: "3px 12px", borderRadius: 2, fontSize: "0.8rem",
    border: active ? "1px solid #1a1a1a" : "1px solid #ccc",
    background: active ? "#1a1a1a" : "#fff",
    color: active ? "#fff" : "#666",
    cursor: "pointer", fontFamily: "Georgia, serif",
  }),
  locTag: (ok) => ({
    display: "inline-block", padding: "2px 10px", borderRadius: 2, fontSize: "0.78rem",
    border: `1px solid ${ok ? "#b5d5b0" : "#f0c080"}`,
    background: ok ? "#f0faf0" : "#fffbf0",
    color: ok ? "#3a7a35" : "#a06010",
    marginRight: 6, marginBottom: 4,
  }),
};

// ═══════════════════════════════════════════════════════════════════════════════
export default function ImportCSVPremium({ collectionId, onImportSuccess }) {
  const [workbook, setWorkbook] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [sheetNames, setSheetNames] = useState([]);
  const [crossSheetDups, setCrossSheetDups] = useState(new Set()); // REF présentes dans un onglet déjà importé
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetRows, setSheetRows] = useState([]);
  const [mapping, setMapping] = useState({});

  // Artiste
  const [artistMode, setArtistMode] = useState("single");
  const [artistFirst, setArtistFirst] = useState("");
  const [artistLast, setArtistLast] = useState("");

  // Lieux
  const [existingLocations, setExistingLocations] = useState([]);
  const [locationPreview, setLocationPreview] = useState([]);

  // Valeurs fixes pour les champs preset
  const [fixedValues, setFixedValues] = useState({});
  // Mode par champ preset: "none" | "fixed" | "column"
  const [presetMode, setPresetMode] = useState({});

  // Gestion des doublons de numéro d'inventaire
  const [dupStrategy, setDupStrategy] = useState("autonumber"); // "autonumber" | "skip"
  const [dupCount, setDupCount] = useState(0); // nb de REF en doublon détectés
  const [applySheetSuffix, setApplySheetSuffix] = useState(false); // suffixe onglet sur les REF

  // Mode enrichissement : UPDATE des fiches existantes par inventory_number
  const [enrichMode, setEnrichMode] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null); // {updated, notFound, skipped}

  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null); // {count, sheetName}

  const headers = useMemo(
    () => (sheetRows.length ? Object.keys(sheetRows[0] || {}) : []),
    [sheetRows]
  );

  // Charger les lieux existants
  useEffect(() => {
    supabase.from("locations").select("id, name").order("name")
      .then(({ data }) => { if (data) setExistingLocations(data); });
  }, []);

  // Aperçu des lieux
  useEffect(() => {
    const col = mapping["location_name"];
    if (!col || !sheetRows.length) { setLocationPreview([]); return; }
    const names = [...new Set(sheetRows.map((r) => String(r[col] || "").trim()).filter(Boolean))];
    setLocationPreview(names.map((name) => ({
      name,
      exists: existingLocations.some((l) => l.name.trim().toLowerCase() === name.toLowerCase()),
    })));
  }, [mapping, sheetRows, existingLocations]);

  // Détecter les doublons de numéro d'inventaire
  useEffect(() => {
    const invCol = mapping["inventory_number"];
    if (!invCol || !sheetRows.length) { setDupCount(0); return; }
    const refs = sheetRows.map((r) => String(r[invCol] || "").trim()).filter(Boolean);
    const counts = refs.reduce((acc, r) => { acc[r] = (acc[r] || 0) + 1; return acc; }, {});
    const dups = Object.values(counts).filter((v) => v > 1).length;
    setDupCount(dups);
  }, [mapping, sheetRows]);

  // Lecture fichier
  async function handleFile(file) {
    setImportFileName(file?.name || "");
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: "array" });
    setWorkbook(wb);
    setSheetNames(wb.SheetNames);
    setArtistMode("single"); setArtistFirst(""); setArtistLast("");
    if (wb.SheetNames.length === 1) loadSheet(wb, wb.SheetNames[0]);
    else { setSelectedSheet(null); setSheetRows([]); setMapping({}); }
  }

  function loadSheet(wb, name) {
    const json = parseSheet(wb.Sheets[name]);
    setSelectedSheet(name);
    setSheetRows(json);
    setMapping(autoMap(json[0]));
    setArtistMode("single"); setArtistFirst(""); setArtistLast("");
    // Pré-remplir les valeurs par défaut définies dans PRESET_FIELDS
    const defaultFixed = {};
    const defaultMode = {};
    Object.entries(PRESET_FIELDS).forEach(([field, cfg]) => {
      if (cfg.defaultFixed !== undefined) {
        defaultFixed[field] = cfg.defaultFixed;
        defaultMode[field] = "fixed";
      }
    });
    setFixedValues(defaultFixed);
    setPresetMode(defaultMode);
  }

  // Résoudre les lieux connus côté client — les inconnus seront créés par l'Edge Function
  function resolveLocations(names) {
    const result = {};
    names.forEach((name) => {
      const existing = existingLocations.find(
        (l) => l.name.trim().toLowerCase() === name.trim().toLowerCase()
      );
      // Stocker DEUX clés : nom exact ET lowercase pour éviter les problèmes de casse
      if (existing) {
        result[name.trim()] = existing.id;
        result[name.trim().toLowerCase()] = existing.id;
      }
    });
    return result;
  }

  // Import
  async function submit() {
    if (!sheetRows.length) { alert("Aucune donnée à importer."); return; }
    if (!collectionId) { alert("Sélectionnez une collection cible."); return; }
    setLoading(true);

    try {
      // Résoudre lieux
      const locationCol = mapping["location_name"];
      const locationNames = locationCol
        ? [...new Set(sheetRows.map((r) => String(r[locationCol] || "").trim()).filter(Boolean))]
        : [];
      const locationMap = locationNames.length ? await resolveLocations(locationNames) : {};

      // ── Appliquer la stratégie doublons sur inventory_number ──────────────
      const invCol = mapping["inventory_number"];
      const refCounter = {};
      const processedRows = sheetRows.map((r) => {
        if (!invCol) return r;
        const ref = String(r[invCol] || "").trim();
        if (!ref) return r;
        refCounter[ref] = (refCounter[ref] || 0) + 1;
        if (refCounter[ref] === 1) return r; // première occurrence → inchangée
        // doublon dans ce fichier
        if (dupStrategy === "autonumber") {
          return { ...r, [invCol]: `${ref}-${refCounter[ref]}` };
        } else {
          return { ...r, __skip: true };
        }
      }).filter((r) => !r.__skip);

      // ── Suffixe d'onglet pour éviter les conflits inter-onglets ─────────
      // Si l'onglet a un nom (autre que le 1er onglet), on suffixe les REF
      // avec une abréviation de l'onglet pour éviter les doublons en base
      const sheetSuffix = selectedSheet && sheetNames.length > 1
        ? "-" + selectedSheet.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase()
        : null;

      const rows = processedRows.map((r) => {
        const obj = {};

        TARGET_FIELDS.forEach((tf) => {
          if (tf === "artist" || tf === "location_name") return;
          const preset = PRESET_FIELDS[tf];
          if (preset) {
            // colonne en priorité, sinon valeur fixe, sinon null
            const fromCol = mapping[tf] ? cleanValue(r[mapping[tf]]) : null;
            const fromFixed = fixedValues[tf] ?? null;
            const raw = fromCol || fromFixed;
            obj[tf] = preset.boolean ? parseBoolean(raw) : (raw || null);
          } else {
            const src = mapping[tf];
            obj[tf] = cleanValue(src ? r[src] : null);
          }
        });

        // Suffixe onglet sur inventory_number — uniquement si c'est le 2e onglet ou plus
        // ET que la REF est dans la liste des doublons inter-onglets
        if (sheetSuffix && invCol && obj.inventory_number && crossSheetDups.has(obj.inventory_number)) {
          obj.inventory_number = obj.inventory_number + sheetSuffix;
        }

        // Artiste
        obj.artist = artistMode === "merge"
          ? [artistFirst ? String(r[artistFirst] || "").trim() : "",
             artistLast  ? String(r[artistLast]  || "").trim() : ""]
              .filter(Boolean).join(" ") || null
          : cleanValue(mapping["artist"] ? r[mapping["artist"]] : null);

        // Lieu → UUID (cherche en exact puis en lowercase)
        if (locationCol) {
          const locName = String(r[locationCol] || "").trim();
          obj.location_id = locName
            ? (locationMap[locName] || locationMap[locName.toLowerCase()] || null)
            : null;
          // Conserver le nom pour que l'Edge Function puisse créer si UUID non trouvé
          if (locName) obj.location_name = locName;
        }

        // Normaliser virgule décimale française dans dimensions (trigger Postgres en a besoin)
        if (obj.dimensions) {
          obj.dimensions = String(obj.dimensions).replace(/,([0-9])/g, ".$1");
        }

        // Dimensions : parser la colonne texte → colonnes plates + specifications JSON
        const d = parseDimensions(obj.dimensions);
        // Colonnes plates (utilisées par l'onglet Spécifications)
        obj.dimensions_height = d.height;
        obj.dimensions_width  = d.width;
        obj.dimensions_depth  = d.depth;
        obj.dimensions_unit   = d.unit;
        // JSONB specifications (utilisé par d'autres parties de l'app)
        obj.specifications = { dimensions: { height: d.height, width: d.width, depth: d.depth, unit: d.unit } };
        // Conserver la chaîne brute dans dimensions pour affichage éventuel
        // obj.dimensions reste inchangé

        // Tags
        obj.tags = typeof obj.tags === "string" && obj.tags
          ? obj.tags.split(",").map((s) => s.trim()).filter(Boolean) : [];

        // Catégorie médium — déduite automatiquement depuis le champ medium
        if (obj.medium && !obj.medium_category) {
          obj.medium_category = inferMediumCategory(obj.medium);
        }

        // Numériques
        ["value", "price", "sale_price", "estimated_value", "acquisition_price"]
          .forEach((f) => { obj[f] = toNumber(obj[f]); });

        // Dates
        if (obj.acquisition_date) obj.acquisition_date = excelDateToISO(obj.acquisition_date);
        if (obj.last_estimation_date) obj.last_estimation_date = excelDateToISO(obj.last_estimation_date);

        // acquisitionDetails
        const hasAcq = obj.acquisition_date || obj.acquisition_price != null
          || obj.acquisition_type || obj.acquisition_source;
        if (hasAcq) {
          obj.acquisitionDetails = JSON.stringify({
            acquisition_date: obj.acquisition_date || "",
            acquisition_price: obj.acquisition_price != null ? String(obj.acquisition_price) : "",
            acquisition_type: obj.acquisition_type || "",
            acquisition_source: obj.acquisition_source || "",
          });
        }

        // Provenance
        if (obj.provenance && typeof obj.provenance === "string") {
          const t = obj.provenance.trim();
          obj.provenance = t
            ? JSON.stringify([{ id: 1, date: null, location: t, description: t }]) : null;
        }

        // title NOT NULL
        if (!obj.title || String(obj.title).trim() === "") obj.title = "Sans titre";

        // Nettoyer les retours chariot dans les champs texte
        ["title","artist","medium","description","condition","conditionNotes",
         "acquisition_type","acquisition_source","typeOfArt","availability"].forEach((f) => {
          if (obj[f] && typeof obj[f] === "string") {
            obj[f] = obj[f].replace(/\r\n|\r|\n/g, " ").trim() || null;
          }
        });

        // Valeurs forcées par défaut
        obj.is_public_for_sale = false;

        obj.collection_id = collectionId;
        return obj;
      });

      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess?.session?.access_token;

      // Lieux manquants (pas encore en base) → l'Edge Function les créera
      const missingLocations = locationNames.filter((n) => !locationMap[n]);

      // Import par chunks de 50 pour éviter le timeout de l'Edge Function
      const CHUNK_SIZE = 50;
      let totalInserted = 0;
      let firstChunk = true;
      // UN seul job d'import pour tout le fichier : créé au 1er chunk, réutilisé ensuite
      // (journal d'import + annulation, ARTDAI §6).
      let importJobId = null;

      for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
        const chunk = rows.slice(i, i + CHUNK_SIZE);
        // Les lieux manquants sont créés seulement au premier chunk
        const { data, error } = await supabase.functions.invoke("import_artworks_self", {
          body: {
            rows: chunk,
            collection_id: collectionId,
            missing_locations: firstChunk ? missingLocations : [],
            source_filename: importFileName || null,
            total_rows: rows.length,
            mapping: mapping || null,
            job_id: importJobId,
            row_offset: i,
          },
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });

        if (error || !data || data.ok === false) {
          alert(`Erreur au chunk ${Math.floor(i/CHUNK_SIZE)+1} : ${error?.message || data?.error || "Erreur inconnue"}. ${totalInserted} œuvres déjà importées.`);
          return;
        }
        if (data.job_id) importJobId = data.job_id;
        totalInserted += data.inserted_count || 0;
        firstChunk = false;
      }

      // Mémoriser les REF de cet onglet pour détecter les doublons inter-onglets
      const importedRefs = new Set(rows.map(r => r.inventory_number).filter(Boolean));
      setCrossSheetDups(prev => new Set([...prev, ...importedRefs]));
      setImportResult({ count: totalInserted, sheetName: selectedSheet });
      // Recharger les lieux pour que le 2e onglet bénéficie des nouveaux lieux créés
      const { data: freshLocs } = await supabase.from("locations").select("id, name").order("name");
      if (freshLocs) setExistingLocations(freshLocs);
      onImportSuccess?.();
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  }

  // ─── Mode enrichissement : UPDATE par inventory_number ───────────────────
  async function handleEnrich() {
    if (!collectionId || !sheetRows.length || !mapping["inventory_number"]) {
      alert("Le mode enrichissement nécessite une colonne 'Numéro d'inventaire' mappée.");
      return;
    }
    setLoading(true);
    setEnrichResult(null);

    try {
      const invCol = mapping["inventory_number"];
      let updated = 0, notFound = 0, skipped = 0;

      // Charger toutes les fiches de la collection avec leur inventory_number
      const { data: existingArtworks, error: fetchErr } = await supabase
        .from("artworks")
        .select("id, inventory_number")
        .eq("collection_id", collectionId);

      if (fetchErr) throw fetchErr;

      // Index par inventory_number pour lookup rapide
      const artworkIndex = {};
      for (const aw of existingArtworks || []) {
        if (aw.inventory_number) {
          artworkIndex[aw.inventory_number.trim().toLowerCase()] = aw.id;
        }
      }

      // Traiter chaque ligne du tableur
      for (const r of sheetRows) {
        const invRef = String(r[invCol] || "").trim();
        if (!invRef) { skipped++; continue; }

        const artworkId = artworkIndex[invRef.toLowerCase()];
        if (!artworkId) { notFound++; continue; }

        // Construire le patch avec uniquement les champs mappés non vides
        const patch = {};
        const UPDATABLE = [
          "title", "artist", "year", "medium", "dimensions", "description",
          "condition", "conditionNotes", "typeOfArt", "subjectMatter",
          "availability", "estimated_value", "value", "currency",
          "acquisition_date", "acquisition_price", "acquisition_type", "acquisition_source",
          "dimensions_height", "dimensions_width", "dimensions_depth", "dimensions_unit",
          "tags", "styles", "signature",
        ];

        UPDATABLE.forEach((tf) => {
          const src = mapping[tf];
          if (!src) return;
          const raw = cleanValue(r[src]);
          if (raw == null || raw === "") return;
          patch[tf] = raw;
        });

        // Artiste
        if (artistMode === "merge" && (artistFirst || artistLast)) {
          const name = [
            artistFirst ? String(r[artistFirst] || "").trim() : "",
            artistLast  ? String(r[artistLast]  || "").trim() : "",
          ].filter(Boolean).join(" ");
          if (name) patch.artist = name;
        } else if (mapping["artist"]) {
          const a = cleanValue(r[mapping["artist"]]);
          if (a) patch.artist = a;
        }

        // Numériques
        ["value", "estimated_value", "acquisition_price"].forEach((f) => {
          if (patch[f] != null) patch[f] = toNumber(patch[f]);
        });

        // Dates
        if (patch.acquisition_date) patch.acquisition_date = excelDateToISO(patch.acquisition_date);

        // Dimensions → colonnes plates
        if (patch.dimensions) {
          patch.dimensions = String(patch.dimensions).replace(/,([0-9])/g, ".$1");
          const d = parseDimensions(patch.dimensions);
          patch.dimensions_height = d.height;
          patch.dimensions_width  = d.width;
          patch.dimensions_depth  = d.depth;
          patch.dimensions_unit   = d.unit;
          patch.specifications    = { dimensions: d };
        }

        // Tags
        if (patch.tags && typeof patch.tags === "string") {
          patch.tags = patch.tags.split(",").map(s => s.trim()).filter(Boolean);
        }

        // acquisitionDetails
        const hasAcq = patch.acquisition_date || patch.acquisition_price != null
          || patch.acquisition_type || patch.acquisition_source;
        if (hasAcq) {
          patch.acquisitionDetails = {
            date:   patch.acquisition_date   || null,
            price:  patch.acquisition_price  ?? null,
            type:   patch.acquisition_type   || null,
            source: patch.acquisition_source || null,
          };
        }

        if (Object.keys(patch).length === 0) { skipped++; continue; }

        const { error: upErr } = await supabase
          .from("artworks")
          .update(patch)
          .eq("id", artworkId);

        if (upErr) {
          console.error("[Enrich] update failed for", invRef, upErr);
          skipped++;
        } else {
          updated++;
        }
      }

      setEnrichResult({ updated, notFound, skipped });
      onImportSuccess?.();
    } catch (e) {
      alert("Erreur enrichissement : " + e.message);
    } finally {
      setLoading(false);
    }
  }

  // ─── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "Georgia, serif" }} className="space-y-6">

      {/* Journal des imports + annulation (ARTDAI §6) — ne s'affiche que s'il y en a */}
      <ImportJobsList />

      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 style={{ fontSize: "1.1rem", letterSpacing: "0.04em", fontWeight: 400 }}>
          Import CSV / XLSX
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Toggle mode enrichissement */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => { setEnrichMode(v => !v); setEnrichResult(null); }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enrichMode ? "bg-black" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${enrichMode ? "translate-x-4" : "translate-x-1"}`} />
            </div>
            <span style={{ fontSize: "0.85rem", color: enrichMode ? "#000" : "#888" }}>
              {enrichMode ? "Mode : Enrichir les fiches existantes" : "Mode : Créer de nouvelles fiches"}
            </span>
          </label>
          <button className="btn-artsy btn-secondary-artsy" onClick={() => {
            const ws = XLSX.utils.json_to_sheet([Object.fromEntries(TARGET_FIELDS.map((k) => [k, ""]))]);
            const wb2 = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb2, ws, "Artworks");
            XLSX.writeFile(wb2, "modele-import-artkiv.xlsx");
          }}>
            Télécharger un modèle
          </button>
        </div>
      </div>

      {/* Bandeau explication mode enrichissement */}
      {enrichMode && (
        <div style={{ border: "1px solid #c9a961", borderRadius: 4, padding: "0.875rem 1.25rem", background: "#fffbf2", fontSize: "0.85rem", color: "#7a5a10" }}>
          <strong>Mode enrichissement :</strong> les données du tableur viennent <em>compléter</em> les fiches déjà créées via l'import ZIP.
          La correspondance se fait par le <strong>numéro d'inventaire</strong> — mappez cette colonne ci-dessous.
          Seuls les champs non vides du tableur seront mis à jour.
        </div>
      )}

      {/* ── Explication pédagogique (visible tant qu'aucun fichier chargé) ── */}
      {!sheetRows.length && (
        <div style={{ border: "1px solid #e5e5e5", borderRadius: 2, padding: "1.5rem 2rem" }}>

          {/* Intro */}
          <p style={{ fontSize: "0.9rem", color: "#444", lineHeight: 1.7, marginBottom: "1.5rem" }}>
            Cet outil vous permet d'importer en une seule opération l'ensemble de vos œuvres
            depuis un fichier Excel ou CSV — qu'il vienne d'une galerie, d'un notaire, ou de
            votre propre tableur. Aucune connaissance technique requise.
          </p>

          {/* 3 étapes */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.5rem", marginBottom: "1.5rem" }}>
            {[
              {
                num: "1",
                titre: "Choisissez votre fichier",
                texte: "Formats acceptés : Excel (.xlsx, .xls) ou CSV. Si votre fichier contient plusieurs onglets, vous choisirez lequel importer. Les colonnes vides en tête de fichier sont ignorées automatiquement.",
              },
              {
                num: "2",
                titre: "Associez vos colonnes",
                texte: "Artkiv détecte automatiquement vos colonnes et fait les correspondances évidentes. Vous vérifiez et ajustez si besoin. Pour l'artiste, vous pouvez fusionner deux colonnes Nom + Prénom en un seul champ.",
              },
              {
                num: "3",
                titre: "Importez en un clic",
                texte: "Un aperçu des 3 premières lignes vous permet de contrôler le résultat avant de lancer. Les lieux de stockage inconnus sont créés automatiquement. Les doublons sont gérés intelligemment.",
              },
            ].map(({ num, titre, texte }) => (
              <div key={num} style={{ display: "flex", gap: "1rem" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", background: "#1a1a1a",
                  color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.8rem", flexShrink: 0, marginTop: 2,
                }}>
                  {num}
                </div>
                <div>
                  <p style={{ fontSize: "0.85rem", fontWeight: 500, color: "#1a1a1a", marginBottom: "0.35rem" }}>
                    {titre}
                  </p>
                  <p style={{ fontSize: "0.8rem", color: "#888", lineHeight: 1.6 }}>
                    {texte}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Formats acceptés */}
          <div style={{ background: "#faf9f6", border: "1px solid #ece9e3", borderRadius: 2, padding: "0.75rem 1rem" }}>
            <p style={{ fontSize: "0.78rem", color: "#888", lineHeight: 1.6 }}>
              <strong style={{ color: "#444" }}>Formats acceptés :</strong> Excel 97–2019 (.xls, .xlsx), CSV (séparateur virgule ou point-virgule).
              {" "}<strong style={{ color: "#444" }}>Taille max recommandée :</strong> 2 000 œuvres par import.
              {" "}Si votre fichier vient d'une galerie ou d'une maison de vente, importez-le tel quel — Artkiv s'adapte à sa structure.
            </p>
          </div>

          {/* Lien modèle */}
          <p style={{ fontSize: "0.78rem", color: "#aaa", marginTop: "0.75rem" }}>
            Vous partez de zéro ?{" "}
            <button
              style={{ background: "none", border: "none", color: "#c9a961", cursor: "pointer", fontSize: "0.78rem", padding: 0, textDecoration: "underline" }}
              onClick={() => {
                const ws = XLSX.utils.json_to_sheet([Object.fromEntries(TARGET_FIELDS.map((k) => [k, ""]))]);
                const wb2 = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb2, ws, "Artworks");
                XLSX.writeFile(wb2, "modele-import-artkiv.xlsx");
              }}
            >
              Téléchargez notre modèle Excel
            </button>
            {" "}et remplissez-le directement.
          </p>
        </div>
      )}

      {/* ── Zone de dépôt fichier ─────────────────────────────────────────── */}
      <label style={{
        display: "block", border: "1px dashed #c9a961", borderRadius: 2,
        padding: sheetRows.length ? "1rem 2rem" : "2rem",
        textAlign: "center", cursor: "pointer",
        background: "#faf9f6", color: "#888", fontSize: "0.9rem",
        transition: "padding 0.2s",
      }}>
        <input type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }}
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
        {sheetRows.length
          ? "Cliquez pour changer de fichier"
          : "Cliquez pour choisir un fichier .xlsx ou .csv"}
      </label>

      {/* Sélecteur onglets */}
      {workbook && sheetNames.length > 1 && (
        <div style={{ background: "#fff8ee", border: "1px solid #c9a961", borderRadius: 2, padding: "1rem 1.25rem" }}>
          <p style={{ fontSize: "0.85rem", color: "#888", marginBottom: "0.75rem" }}>
            Votre fichier contient plusieurs onglets. Lequel contient vos œuvres ?
          </p>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {sheetNames.map((name) => (
              <button key={name} onClick={() => loadSheet(workbook, name)}
                style={S.toggleBtn(selectedSheet === name)}>
                {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mapping */}
      {sheetRows.length > 0 && (
        <>
          <div style={S.box}>
            <h4 style={S.sectionTitle}>Associer vos colonnes</h4>
            <div style={{ marginBottom: "1rem" }}>
              <p style={{ fontSize: "0.8rem", color: "#666", marginBottom: "0.4rem" }}>
                <strong>{headers.length} colonne(s)</strong> détectée(s)
                {selectedSheet ? ` dans l'onglet « ${selectedSheet} »` : ""}.
                Les correspondances évidentes ont été faites automatiquement.
              </p>
              <p style={{ fontSize: "0.78rem", color: "#aaa", lineHeight: 1.6 }}>
                Pour chaque champ Artkiv (colonne de gauche), choisissez la colonne correspondante dans votre fichier.
                Les champs non mappés seront laissés vides — vous pourrez les compléter fiche par fiche ensuite.
                Les champs marqués <span style={{ color: "#c9a961" }}>●</span> proposent aussi une valeur commune à appliquer à toutes les œuvres.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-2">
              {TARGET_FIELDS.map((tf) => {

                // ── Champ Artiste ──────────────────────────────────────────
                if (tf === "artist") return (
                  <div key="artist" style={S.specialBox}>
                    <p style={{ fontSize: "0.85rem", color: "#444", fontWeight: 500, marginBottom: "0.5rem" }}>
                      Artiste
                    </p>
                    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
                      <button style={S.toggleBtn(artistMode === "single")}
                        onClick={() => setArtistMode("single")}>
                        Une seule colonne
                      </button>
                      <button style={S.toggleBtn(artistMode === "merge")}
                        onClick={() => setArtistMode("merge")}>
                        Fusionner Nom + Prénom
                      </button>
                    </div>

                    <p style={{ fontSize: "0.78rem", color: "#aaa", lineHeight: 1.6, marginBottom: "0.6rem" }}>
                      Si votre fichier a une seule colonne "Artiste" avec le nom complet, choisissez <em>Une seule colonne</em>.
                      Si les colonnes Nom et Prénom sont séparées (fréquent dans les inventaires de galeries), choisissez <em>Fusionner</em> — Artkiv les assemblera automatiquement.
                    </p>

                    {artistMode === "single" ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={S.label}>Colonne artiste</div>
                        <select style={S.select} value={mapping["artist"] || ""}
                          onChange={(e) => setMapping((m) => ({ ...m, artist: e.target.value || undefined }))}>
                          <option value="">— ne pas importer</option>
                          {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={S.label}>Colonne Prénom</div>
                          <select style={S.select} value={artistFirst}
                            onChange={(e) => setArtistFirst(e.target.value)}>
                            <option value="">—</option>
                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={S.label}>Colonne Nom</div>
                          <select style={S.select} value={artistLast}
                            onChange={(e) => setArtistLast(e.target.value)}>
                            <option value="">—</option>
                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                        {(artistFirst || artistLast) && sheetRows[0] && (
                          <p style={{ fontSize: "0.8rem", color: "#888", marginTop: 2 }}>
                            Aperçu 1re ligne :{" "}
                            <strong style={{ color: "#1a1a1a" }}>
                              {[
                                artistFirst ? String(sheetRows[0][artistFirst] || "").trim() : "",
                                artistLast  ? String(sheetRows[0][artistLast]  || "").trim() : "",
                              ].filter(Boolean).join(" ")}
                            </strong>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );

                // ── Champ Lieu ─────────────────────────────────────────────
                if (tf === "location_name") return (
                  <div key="location_name" style={S.specialBox}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: locationPreview.length ? "0.75rem" : 0 }}>
                      <div style={{ fontSize: "0.85rem", color: "#444", fontWeight: 500, marginRight: 8, flexShrink: 0 }}>
                        Lieu de stockage
                      </div>
                      <select style={{ ...S.select, maxWidth: 320 }} value={mapping["location_name"] || ""}
                        onChange={(e) => setMapping((m) => ({ ...m, location_name: e.target.value || undefined }))}>
                        <option value="">— ne pas importer</option>
                        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>

                    <p style={{ fontSize: "0.78rem", color: "#aaa", lineHeight: 1.6, marginBottom: "0.5rem" }}>
                      Indiquez la colonne qui contient le lieu de stockage (ex : "Réserve Paris", "Domicile", "IVRY / Capstone").
                      Les lieux déjà enregistrés dans Artkiv sont reconnus automatiquement (<span style={{color:"#3a7a35"}}>✓</span>).
                      Les nouveaux seront créés pour vous lors de l'import (<span style={{color:"#a06010"}}>+</span>) — vous pourrez les compléter avec une adresse ensuite.
                    </p>

                    {locationPreview.length > 0 && (
                      <div>
                        <p style={{ fontSize: "0.78rem", color: "#888", marginBottom: "0.4rem" }}>
                          Lieux détectés dans votre fichier :
                        </p>
                        <div>
                          {locationPreview.map(({ name, exists }) => (
                            <span key={name} style={S.locTag(exists)}>
                              {exists ? "✓" : "+"} {name}
                            </span>
                          ))}
                        </div>
                        {locationPreview.some((l) => !l.exists) && (
                          <p style={{ fontSize: "0.75rem", color: "#a06010", marginTop: "0.4rem" }}>
                            Les lieux marqués <strong>+</strong> seront créés automatiquement lors de l'import.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );

                // ── Champs preset (valeur fixe ou colonne) ────────────
                if (PRESET_FIELDS[tf]) {
                  const preset = PRESET_FIELDS[tf];
                  const mode = presetMode[tf] || "none";
                  return (
                    <div key={tf} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={S.label}>{FIELD_LABELS[tf] || tf}</div>
                        <div style={{ display: "flex", gap: 4 }}>
                          {["none","fixed","column"].map((m) => (
                            <button key={m} style={{
                              ...S.toggleBtn(mode === m),
                              padding: "2px 8px", fontSize: "0.75rem",
                            }} onClick={() => setPresetMode((p) => ({ ...p, [tf]: m }))}>
                              {m === "none" ? "—" : m === "fixed" ? "Valeur fixe" : "Colonne"}
                            </button>
                          ))}
                        </div>
                      </div>
                      {mode === "fixed" && (
                        <div style={{ paddingLeft: 228 }}>
                          <select style={{ ...S.select, background: "#fffdf7", maxWidth: 220 }}
                            value={fixedValues[tf] || ""}
                            onChange={(e) => setFixedValues((v) => ({ ...v, [tf]: e.target.value || undefined }))}>
                            <option value="">— choisir…</option>
                            {preset.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                          {FIELD_HINTS[tf] && (
                            <p style={{ fontSize: "0.75rem", color: "#aaa", marginTop: 4, lineHeight: 1.5, maxWidth: 380 }}>
                              {FIELD_HINTS[tf]}
                            </p>
                          )}
                        </div>
                      )}
                      {mode === "column" && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 228 }}>
                          <select style={{ ...S.select, background: mapping[tf] ? "#fffdf7" : "#fff", maxWidth: 220 }}
                            value={mapping[tf] || ""}
                            onChange={(e) => setMapping((m2) => ({ ...m2, [tf]: e.target.value || undefined }))}>
                            <option value="">— ne pas importer</option>
                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                }

                // ── Champs normaux ─────────────────────────────────────────
                return (
                  <div key={tf} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={S.label}>{FIELD_LABELS[tf] || tf}</div>
                    <select
                      style={{ ...S.select, background: mapping[tf] ? "#fffdf7" : "#fff" }}
                      value={mapping[tf] || ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [tf]: e.target.value || undefined }))}
                    >
                      <option value="">— ne pas importer</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>

            <p style={S.hint}>
              Format dimensions : <strong>Hauteur × Largeur</strong> ou <strong>Hauteur × Largeur × Profondeur</strong> (en cm).
            </p>
          </div>

          {/* Aperçu */}
          <div style={{ ...S.box, overflowX: "auto" }}>
            <h4 style={S.sectionTitle}>Aperçu — 3 premières lignes</h4>
            <table style={{ fontSize: "0.8rem", borderCollapse: "collapse", minWidth: 400 }}>
              <thead>
                <tr>
                  {TARGET_FIELDS.filter((tf) =>
                    tf === "artist"
                      ? (artistMode === "single" ? !!mapping["artist"] : !!(artistFirst || artistLast))
                      : !!mapping[tf]
                  ).map((tf) => (
                    <th key={tf} style={{ padding: "4px 10px", borderBottom: "1px solid #ddd", textAlign: "left", color: "#888", fontWeight: 400 }}>
                      {FIELD_LABELS[tf] || tf}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sheetRows.slice(0, 3).map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #f0f0f0" }}>
                    {TARGET_FIELDS.filter((tf) =>
                      tf === "artist"
                        ? (artistMode === "single" ? !!mapping["artist"] : !!(artistFirst || artistLast))
                        : !!mapping[tf]
                    ).map((tf) => {
                      let val = "";
                      if (tf === "artist") {
                        val = artistMode === "merge"
                          ? [artistFirst ? String(r[artistFirst] || "").trim() : "",
                             artistLast  ? String(r[artistLast]  || "").trim() : ""]
                              .filter(Boolean).join(" ")
                          : String(r[mapping["artist"]] ?? "");
                      } else {
                        val = String(r[mapping[tf]] ?? "");
                      }
                      return (
                        <td key={tf} style={{ padding: "4px 10px", color: "#333", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {val}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Choix suffixe inter-onglets ─────────────────────────────── */}
          {mapping["inventory_number"] && sheetNames.length > 1 && selectedSheet && selectedSheet !== sheetNames[0] && (
            <div style={{
              border: "1px solid #e0ddd8", borderRadius: 2,
              padding: "0.75rem 1rem", background: "#fafaf8", fontSize: "0.82rem", color: "#444",
            }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={applySheetSuffix}
                  onChange={e => setApplySheetSuffix(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span>
                  <strong>Ajouter un suffixe aux numéros d'inventaire</strong>
                  {applySheetSuffix ? (
                    <span style={{ color: "#c9a961" }}>
                      {" "}— suffixe <strong>-{selectedSheet.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase()}</strong> activé
                      (ex : MA-37 → MA-37-{selectedSheet.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase()}).
                    </span>
                  ) : (
                    <span style={{ color: "#888" }}>
                      {" "}— à cocher uniquement si les numéros d'inventaire de cet onglet sont identiques à ceux déjà importés et qu'il faut les distinguer.
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}

          {/* ── Avertissement doublons ─────────────────────────────────── */}
          {dupCount > 0 && mapping["inventory_number"] && (
            <div style={{
              border: "1px solid #f0c080", borderRadius: 2,
              padding: "1rem 1.25rem", background: "#fffbf0",
            }}>
              <p style={{ fontSize: "0.85rem", color: "#a06010", marginBottom: "0.75rem" }}>
                <strong>{dupCount} numéro{dupCount > 1 ? "s" : ""} d'inventaire en doublon</strong> détecté{dupCount > 1 ? "s" : ""} dans votre fichier.
                <br />
                <span style={{ fontSize: "0.8rem", color: "#888", marginTop: 4, display: "block" }}>
                  Cela arrive quand une galerie utilise la même référence pour plusieurs œuvres d'un même lot (ex : plusieurs tirages sous PP-54).
                </span>
              </p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  style={S.toggleBtn(dupStrategy === "autonumber")}
                  onClick={() => setDupStrategy("autonumber")}
                >
                  Auto-numéroter (PP-54 → PP-54-2, PP-54-3…)
                </button>
                <button
                  style={S.toggleBtn(dupStrategy === "skip")}
                  onClick={() => setDupStrategy("skip")}
                >
                  Ignorer les doublons
                </button>
              </div>
              {dupStrategy === "autonumber" && (
                <p style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.5rem" }}>
                  La première occurrence garde sa référence d'origine. Les suivantes reçoivent un suffixe -2, -3…
                </p>
              )}
              {dupStrategy === "skip" && (
                <p style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.5rem" }}>
                  Seule la première occurrence de chaque référence sera importée.
                </p>
              )}
            </div>
          )}

          {/* ── Écran de succès ───────────────────────────────────────── */}
          {importResult && (
            <div style={{
              border: "1px solid #b5d5b0", borderRadius: 2,
              padding: "1.25rem 1.5rem", background: "#f0faf0",
            }}>
              <p style={{ fontSize: "1rem", color: "#2a6a25", marginBottom: "0.75rem" }}>
                ✓ <strong>{importResult.count} œuvre(s)</strong> importée(s) avec succès
                {importResult.sheetName ? ` depuis l'onglet « ${importResult.sheetName} »` : ""}.
              </p>

              {/* Proposer les autres onglets si disponibles */}
              {workbook && sheetNames.filter(n => n !== importResult.sheetName).length > 0 && (
                <div style={{ marginBottom: "1rem" }}>
                  <p style={{ fontSize: "0.85rem", color: "#444", marginBottom: "0.5rem" }}>
                    Votre fichier contient d'autres onglets. Voulez-vous les importer aussi ?
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {sheetNames.filter(n => n !== importResult.sheetName).map(name => (
                      <button key={name}
                        style={{
                          padding: "0.4rem 1rem", borderRadius: 2, fontSize: "0.85rem",
                          border: "1px solid #1a1a1a", background: "#1a1a1a",
                          color: "#fff", cursor: "pointer", fontFamily: "Georgia, serif",
                        }}
                        onClick={() => {
                          setImportResult(null);
                          loadSheet(workbook, name);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}>
                        Importer l'onglet « {name} »
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button
                  style={{
                    padding: "0.4rem 1.25rem", borderRadius: 2, fontSize: "0.85rem",
                    border: "1px solid #1a1a1a", background: "#fff",
                    color: "#1a1a1a", cursor: "pointer", fontFamily: "Georgia, serif",
                  }}
                  onClick={() => {
                    setImportResult(null);
                    setSheetRows([]);
                    setMapping({});
                    setWorkbook(null);
                    setSheetNames([]);
                    setSelectedSheet(null);
                    setFixedValues({});
                    setPresetMode({});
                    setDupCount(0);
                  }}>
                  Nouvel import
                </button>
                <a href={`/collections/${collectionId}`}
                  style={{
                    padding: "0.4rem 1.25rem", borderRadius: 2, fontSize: "0.85rem",
                    border: "1px solid #1a1a1a", background: "#1a1a1a",
                    color: "#fff", cursor: "pointer", fontFamily: "Georgia, serif",
                    textDecoration: "none", display: "inline-block",
                  }}>
                  Voir ma collection →
                </a>
              </div>
            </div>
          )}

          {/* Résultat enrichissement */}
          {enrichResult && (
            <div style={{ border: "1px solid #b5d5b0", borderRadius: 4, padding: "1rem 1.5rem", background: "#f0faf0", fontSize: "0.9rem" }}>
              <strong style={{ color: "#3a7a35" }}>Enrichissement terminé</strong>
              <div style={{ marginTop: 8, color: "#444", lineHeight: 1.8 }}>
                ✅ {enrichResult.updated} fiche{enrichResult.updated > 1 ? "s" : ""} mises à jour<br />
                {enrichResult.notFound > 0 && <>⚠️ {enrichResult.notFound} numéro{enrichResult.notFound > 1 ? "s" : ""} d'inventaire non trouvé{enrichResult.notFound > 1 ? "s" : ""} en base<br /></>}
                {enrichResult.skipped > 0 && <>— {enrichResult.skipped} ligne{enrichResult.skipped > 1 ? "s" : ""} ignorée{enrichResult.skipped > 1 ? "s" : ""} (sans données)<br /></>}
              </div>
            </div>
          )}

          {!importResult && !enrichResult && (
          <button
            className="btn-artsy btn-primary-artsy"
            onClick={enrichMode ? handleEnrich : submit}
            disabled={loading}
            style={{ minWidth: 200 }}
          >
            {loading
              ? (enrichMode ? "Enrichissement en cours…" : "Import en cours…")
              : enrichMode
                ? `Enrichir ${sheetRows.length} fiche(s)`
                : `Importer ${sheetRows.length} œuvre(s)`
            }
          </button>
          )}
        </>
      )}
    </div>
  );
}